/**
 * Release manifest and OS detection for the public HOWIE Companion download page.
 *
 * The manifest lives at /companion/desktop-releases.json (apps/howie/public) so a
 * release only edits that file. An empty `url` means the build isn't out yet and
 * the page shows a disabled button.
 */

export const DESKTOP_RELEASES_URL = '/companion/desktop-releases.json';

/** Minimum macOS for the desktop app (ScreenCaptureKit system-audio capture). */
export const MAC_MIN_OS = 'macOS 14.4 or later';

export type MacArch = 'universal' | 'arm64' | 'x64';

export interface DesktopReleases {
  version: string;
  /** `intelUrl`: the Intel build, when `url` is the Apple silicon one (per-arch DMGs). */
  mac: { url: string; sha256: string; arch: MacArch | string; intelUrl?: string };
  windows: { url: string; sha256: string; signed: boolean };
}

export type VisitorOs = 'mac' | 'windows' | 'other';

export const EMPTY_RELEASES: DesktopReleases = {
  version: '',
  mac: { url: '', sha256: '', arch: 'universal' },
  windows: { url: '', sha256: '', signed: false },
};

function text(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Accepts whatever the JSON holds; anything missing or malformed reads as "not released yet". */
export function parseDesktopReleases(raw: unknown): DesktopReleases {
  const data = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const mac = (data.mac && typeof data.mac === 'object' ? data.mac : {}) as Record<string, unknown>;
  const windows = (data.windows && typeof data.windows === 'object' ? data.windows : {}) as Record<string, unknown>;
  return {
    version: text(data.version),
    mac: { url: text(mac.url), sha256: text(mac.sha256), arch: text(mac.arch) || 'universal', ...(text(mac.intelUrl) ? { intelUrl: text(mac.intelUrl) } : {}) },
    // Unsigned unless the manifest says otherwise, so the "Run anyway" guide is never hidden by mistake.
    windows: { url: text(windows.url), sha256: text(windows.sha256), signed: windows.signed === true },
  };
}

export function macArchLabel(arch: string): string {
  if (arch === 'arm64') return 'Apple silicon';
  if (arch === 'x64') return 'Intel';
  return 'Apple silicon and Intel';
}

interface NavigatorLike {
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
  userAgentData?: { platform?: string };
}

export function detectVisitorOs(nav: NavigatorLike | undefined): VisitorOs {
  if (!nav) return 'other';
  const platform = `${nav.userAgentData?.platform ?? ''} ${nav.platform ?? ''} ${nav.userAgent ?? ''}`;
  if (/iPhone|iPad|iPod|Android/i.test(platform)) return 'other';
  if (/Win/i.test(platform)) return 'windows';
  if (/Mac/i.test(platform)) {
    // iPadOS reports itself as a Mac; a touch screen gives it away.
    return (nav.maxTouchPoints ?? 0) > 1 ? 'other' : 'mac';
  }
  return 'other';
}
